/**
 * PF Revamp — v2 available-creatives query.
 *
 * ONE POST replaces v1 `/order/configuration/creatives`:
 *   `POST /workspace/{ws}/sessions/{sessionId}/creatives?name=&page=&size=`
 * → `AvailableCreativesV2DTO` = `personalCreatives` + `workspaceCreatives`, each
 *   carrying the travel cost booking that creative adds to the order.
 *
 * WHY a new endpoint was needed (the same numeric-id gap ESC-W4-01 names
 * for the payment intent): v1 takes `selectedProductIds` — numeric legacy
 * Pipedrive ids. A v2 cart carries product codes only, so the
 * revamp could only ever send an empty product set, and the BE then filtered out
 * every creative. This query sends the same `productCodes` the rest of the v2
 * flow speaks.
 *
 * POST-as-READ: the endpoint is a pure projection of
 * (session × productCodes × name × page) with no side effects, so it is a
 * `useQuery` with the body + filters in the cache key (mirrors
 * `purchaseFlowRevampInstructions.query.ts`), NOT a mutation. Consequences, all
 * desired: a cart edit that changes the resolved code set refetches; the picker
 * keeps the previous list on screen while it does; and the search term is
 * debounced into the key so typing costs one request, not one per keystroke.
 *
 * `ws` / `sessionId` / body are resolved INTERNALLY (store + UserDataContext +
 * auth0), never passed by call sites — same contract as `useCatalogue` and
 * `useInstructionOptions`, so every consumer shares one cache entry.
 *
 * What is NOT sent, by design: address, coordinates, country, `clientUserId` and
 * a body `workspaceId`. The BE derives the shoot location from the session's
 * property — the same address v2 placement derives the order location from — so
 * the travel cost quoted here is the one the placed order bills. Re-supplying
 * them from the FE is how the two drift apart.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import type {
  AvailableCreativeV2DTO,
  AvailableCreativesRequestV2DTO,
  AvailableCreativesV2DTO,
  CartItem,
  ProductCode,
  ResolvedCatalogue
} from 'models/purchaseFlowRevamp'
import { EntityKeys, QueryType } from 'utils/reactQuery'
import { useAPIV2 } from 'utils/API'
import { useAuth0 } from 'utils/auth'
import { useDebouncedMemo } from 'utils/helpers'
import { usePfStore } from 'components/pages/PurchaseFlowRevamp/store'

import { usePfCatalogue } from './purchaseFlowRevamp.query'
import { resolveCartProductCodes } from './purchaseFlowRevampProductCodes'
import { usePfSessionWorkspaceId } from 'components/pages/PurchaseFlowRevamp/routing/usePfSessionWorkspaceId'

enum Endpoints {
  /** POST — v2 session-bound favourite-creative listing. */
  SESSION_CREATIVES = '/workspace/{workspaceId}/sessions/{sessionId}/creatives',
}

/** Status vocabulary shared with `useRevampCreatives`. */
export type AvailableCreativesStatus = 'idle' | 'loading' | 'ready' | 'error'

/** The BE caps a page at 10 because it runs the Google distance-matrix call
 *  twice (personal + workspace bucket), so 10 is both the default and the max.
 *  The picker shows the closest N, exactly as the legacy card did — there is no
 *  paging UI to drive a second page. */
const PAGE_SIZE = 10

/** Debounce for the name filter, matching the legacy card's 350ms. */
const SEARCH_DEBOUNCE_MS = 350

/* -------------------------------------------------------------------------- *
 *  Pure helpers
 * -------------------------------------------------------------------------- */

/**
 * Build the request body from the cart × resolved catalogue. Returns `null` when
 * no product code resolves — the wire requires `minItems: 1`, so the hook stays
 * disabled (`status: 'idle'`) instead of posting an empty array and getting back
 * a list of creatives vetted against nothing.
 */
export const buildAvailableCreativesBody = (
  items: ReadonlyArray<CartItem>,
  catalogue: ResolvedCatalogue | undefined
): AvailableCreativesRequestV2DTO | null => {
  const productCodes = resolveCartProductCodes(items, catalogue)
  if (productCodes.length === 0) return null
  return { productCodes }
}

/** What booking a creative adds to the order. `productCode` is what the payment
 *  intent needs as its own line — the order line itself is derived server-side
 *  at placement (same split as the key-pickup travel fee, V-16). */
export interface RevampCreativeTravelCost {
  productCode: ProductCode
  amount: number
  currency: string
  quantity: number
}

/** One selectable creative. `id` is stringified because the store keeps
 *  `ctBookingId` as a string and `serializePlacement` is what converts it back
 *  to the numeric `selectedFavouriteCreativeId` the wire wants. */
export interface RevampCreative {
  id: string
  fullName: string
  phoneNumber?: string
  travelCost?: RevampCreativeTravelCost
}

const mapCreative = (wire: AvailableCreativeV2DTO): RevampCreative => ({
  id: String(wire.id),
  fullName: wire.fullName,
  phoneNumber: wire.phoneNumber ?? undefined,
  travelCost: wire.travelCost
    ? {
      productCode: wire.travelCost.productCode,
      amount: wire.travelCost.total.amount,
      currency: wire.travelCost.total.currency,
      quantity: wire.travelCost.quantity,
    }
    : undefined,
})

/** The two buckets, kept apart because the picker tables them separately
 *  ("previously booked" vs "workspace favourites"), exactly as the legacy card
 *  did. */
export interface RevampCreativeBuckets {
  personal: RevampCreative[]
  workspace: RevampCreative[]
}

/**
 * Project the response into the two tables, personal first (a creative the
 * client has worked with themselves is the better offer) and de-duplicated
 * ACROSS the buckets: they legitimately overlap when a workspace colleague
 * booked the same creative, and listing that person twice would give them two
 * "Choose" buttons with no way to tell the rows apart.
 */
export const bucketAvailableCreatives = (dto: AvailableCreativesV2DTO | undefined): RevampCreativeBuckets => {
  const personal = (dto?.personalCreatives ?? []).map(mapCreative)
  const claimed = new Set(personal.map((creative) => creative.id))
  const workspace = (dto?.workspaceCreatives ?? [])
    .map(mapCreative)
    .filter((creative) => !claimed.has(creative.id))
  return { personal, workspace }
}

/* -------------------------------------------------------------------------- *
 *  Hook
 * -------------------------------------------------------------------------- */

/** Options of {@link useAvailableCreatives}. */
export interface UseAvailableCreativesOptions {
  /** Gate the request entirely — the picker only asks once the client has opted
   *  into booking a specific creative. */
  enabled?: boolean
  /** Name filter. Debounced internally before it reaches the cache key. */
  name?: string
}

/** Result of {@link useAvailableCreatives}. */
export interface UseAvailableCreativesResult {
  /** Both buckets, de-duplicated — for finding a creative by id. */
  creatives: RevampCreative[]
  /** The buckets kept apart — for the picker's two tables. */
  buckets: RevampCreativeBuckets
  status: AvailableCreativesStatus
  /** `query.error?.message`, kept a string for the public hook shape. */
  error?: string
  /** `true` when the request never fired (not enabled, or no session / workspace
   *  / resolvable cart), so callers render an idle state rather than an empty
   *  one. */
  isSkipped: boolean
  /** Asks again after a failure. */
  refetch: () => void
}

/**
 * React Query hook — the creatives bookable for the current session + cart.
 *
 * @example
 * const { creatives, status } = useAvailableCreatives({ enabled, name: search })
 */
export function useAvailableCreatives(options?: UseAvailableCreativesOptions): UseAvailableCreativesResult {
  const { enabled = true, name } = options ?? {}

  const api = useAPIV2<Endpoints>()
  const { loading: authLoading } = useAuth0()

  const items = usePfStore((s) => s.items)
  const sessionId = usePfStore((s) => s.draft.sessionId)

  // Workspace: admin → picked target; client → its property workspace. Same
  // resolution as `usePfCatalogue` / `useInstructionOptions`, so all three agree.
  const ws = usePfSessionWorkspaceId()

  const catalogue = usePfCatalogue().data

  const body = useMemo(() => buildAvailableCreativesBody(items, catalogue), [items, catalogue])

  // Debounced into the CACHE KEY rather than into an effect: React Query then
  // owns the request, so an in-flight search cannot land after a newer one.
  const search = useDebouncedMemo(() => name?.trim() || undefined, [name], SEARCH_DEBOUNCE_MS)

  const canQuery = enabled && Boolean(ws && sessionId) && body !== null

  const query = useQuery({
    queryKey: [
      EntityKeys.PF_REVAMP_CREATIVES,
      QueryType.LIST,
      { ws, sessionId, body, search },
    ],
    queryFn: async (): Promise<AvailableCreativesV2DTO> => {
      if (!ws || !sessionId || !body) throw new Error('available creatives called without a valid body')

      return api.post<AvailableCreativesV2DTO>(
        Endpoints.SESSION_CREATIVES,
        { workspaceId: ws, sessionId },
        body,
        false,
        {
          params: {
            page: 0,
            size: PAGE_SIZE,
            name: search,
          },
        }
      )
    },
    // Roles decide the mimetype and resolve late; see the catalogue query.
    enabled: canQuery && !authLoading,
    // Distances and the past-work relation do not change inside a purchase, but
    // the list is re-keyed on cart + search, so a long stale window is safe.
    staleTime: 5 * 60_000,
    retry: 1,
    // Keep the previous list on screen while a search / cart edit refetches, so
    // the dropdown never collapses back to its empty state mid-typing.
    placeholderData: (prev) => prev,
  })

  const isSkipped = !canQuery

  const status: AvailableCreativesStatus = isSkipped
    ? 'idle'
    : query.isError
      ? 'error'
      : query.data
        ? 'ready'
        : 'loading'

  const buckets = useMemo(() => bucketAvailableCreatives(query.data), [query.data])

  const empty = { personal: [], workspace: [] }

  return {
    creatives: isSkipped ? [] : [...buckets.personal, ...buckets.workspace],
    buckets: isSkipped ? empty : buckets,
    status,
    error: query.error?.message,
    isSkipped,
    refetch: query.refetch,
  }
}
